import React, { useMemo, useState, useEffect, useRef } from "react";

function formatYYYYMMDD(date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Rome",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function formatLabelIT(date) {
  const weekday = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", weekday: "short" }).format(date);
  const day = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", day: "2-digit" }).format(date);
  const month = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", month: "long" }).format(date);
  const year = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", year: "numeric" }).format(date);
  return `${weekday} ${day} ${month} ${year}`;
}

export default function SelectGiorni({ value, onChange, giorni = 14, id = "select-giorni" }) {
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const options = useMemo(() => {
    return Array.from({ length: giorni }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const val = formatYYYYMMDD(d);   // value in AAAA-MM-GG
      const label = formatLabelIT(d);  // etichetta leggibile
      return { value: val, label };
    });
  }, [giorni, today]);

  // Stato interno: usato solo quando il componente NON è controllato
  const [internalValue, setInternalValue] = useState(value ?? options[0]?.value ?? "");

  // Traccia l'ultimo valore notificato al parent per evitare doppie chiamate
  const lastNotifiedRef = useRef(null);

  // Se la prop `value` cambia (componente controllato), allinea lo stato interno
  useEffect(() => {
    if (value != null && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  // All'avvio (e quando cambiano le opzioni o la prop `value`) imposta un valore valido
  // e chiama onChange se il componente è non controllato.
  useEffect(() => {
    const first = options[0]?.value ?? "";
    let target = (value ?? internalValue ?? first) || "";

    // Se l'attuale non è più valido (o è vuoto), rimpiazza con la prima opzione
    if (!options.some(o => o.value === target)) {
      target = first;
    }

    // Aggiorna lo stato interno se serve
    if (target && target !== internalValue) {
      setInternalValue(target);
    }

    // Notifica subito il parent SE il componente è non controllato
    if (value == null && target && lastNotifiedRef.current !== target) {
      lastNotifiedRef.current = target;
      onChange?.(target);
    }
  }, [options, value]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleChange(e) {
    const v = e.target.value;
    setInternalValue(v);
    lastNotifiedRef.current = v;
    onChange?.(v); // restituisce AAAA-MM-GG
  }

  const selected = value ?? internalValue ?? "";

  return (
    <select className="form-control" id={id} value={selected} onChange={handleChange}>
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
